/**
 * Coordinator Initialization
 *
 * Initializes all coordinators and registers their message handlers with the MessageRouter.
 * This is the single entry point for the new coordinator-based architecture.
 *
 * Called at app startup when USE_MESSAGE_ROUTER feature flag is enabled.
 */

import { PavilionCoordinator } from './pavilion/PavilionCoordinator';
import { NonsoleSanitizationMonitor } from './pavilion/NonsoleSanitizationMonitor';
import { GameLaunchCoordinator } from './game-launch/GameLaunchCoordinator';
import { SessionCoordinator } from './session/SessionCoordinator';
import { PeerCoordinator } from './peer/PeerCoordinator';
import { MediaCoordinator } from './media/MediaCoordinator';
import { StatefulMacroCoordinator } from './macro/StatefulMacroCoordinator';
import { NativeMacroLaunchManager } from './macro/managers/NativeMacroLaunchManager';
import { initializeMacroAnalysis } from './macro-analysis/initializeMacroAnalysis';
import { MacroBundleSocketLoader } from '../services/playjector/MacroBundleSocketLoader';
import { ScheduledScreenshotWatcher } from './scheduled-watcher/ScheduledScreenshotWatcher';
import { WatcherCheckRunner } from './scheduled-watcher/WatcherCheckRunner';
import { PeerLiteConnectionFactory } from '@playcastdotio/PeerCore';
import { AppCoordinator } from './app/AppCoordinator';
import { realtimeMessageRouter, playjectorMessageRouter } from '../services/MessageRouter';
import { replicationRouter } from './ReplicationRouter';
import { generators } from '@playcastdotio/Messaging';
import { authState, machineIdState, loginProfileState, driverState, sanitizationState, macroRunningState, hostState, shadowcamBindState } from '../api/stores/shared'; // TODO-STRANGLER-FIG: Temporary legacy adapter
import { wireMacroRunningReplication } from './macro/wireMacroRunningReplication';
import type { DisplaySourceManager } from './media/managers/DisplaySourceManager';
import type { RestartManager } from './restart/RestartManager';
import { initRestartManager } from './restart/restartInit';
import type { AudioManager } from './media/managers/AudioManager';
import type { DisplayOutputManager } from './media/managers/DisplayOutputManager';
import { serviceContainer } from '../services/ServiceContainer';
import { RealtimeSignalingService } from '../services/peer/RealtimeSignalingService';
import { getStateStore } from '../api/stores';
import type { AdminPeerHandler } from './peer/handlers/AdminPeerHandler';
import type { ConnectedUsersIpcSyncManager } from './session/managers/ConnectedUsersIpcSyncManager';
import {
  USE_MESSAGE_ROUTER,
  USE_PAVILION_COORDINATOR,
  USE_SESSION_COORDINATOR,
  USE_PLAYJECTOR_COORDINATOR,
  USE_MEDIA_COORDINATOR,
  USE_BEACON_COORDINATOR,
  USE_PROVISIONING_COORDINATOR,
} from '../config/featureFlags';
import { BeaconCoordinator } from './beacon/BeaconCoordinator';
import { BeaconMediaManagerInitializer } from './beacon/BeaconMediaManagerInitializer';
import { ProvisioningCoordinator } from './provisioning/ProvisioningCoordinator';
import { NonsoleExitCoordinator } from './nonsole/NonsoleExitCoordinator';
import { wireScheduledWatcherEvents } from './scheduled-watcher/watcherEventWiring';
import { GamingScoreCoordinator } from './gamingScore/GamingScoreCoordinator';
import { GamingScoreApiService } from './gamingScore/GamingScoreApiService';
import constants from '@playcastdotio/GlobalConfig';
import { setLegacyMatchmakerShadowcamHealthSource } from '../api/modules/managers/matchmaker';
import type { LocalHostSessionCoordinator } from './local-host-session/LocalHostSessionCoordinator';
import { initializePeerStack } from './initializePeerStack';
import { initializeMacroPackages, type MacroPackageRuntime } from './macro/initializeMacroPackages';
import { ipcReceive, macroPackageReceive } from '../api/messaging/protocols/ipc';

// Create coordinator singletons here (lazy, avoids GlobalConfig in tests)
// appCoordinator must be first — GameLaunchCoordinator injects it into GameLifecycleManager (avoids init↔game-launch cycle)
export const appCoordinator = new AppCoordinator(serviceContainer.analyticsManager);
export const pavilionCoordinator = new PavilionCoordinator(
  serviceContainer.realtimeService,
  serviceContainer.campaignConfigService,
  serviceContainer.analyticsManager
);
export const gameLaunchCoordinator = new GameLaunchCoordinator(
  serviceContainer.playjectorService,
  appCoordinator,
  serviceContainer.analyticsManager,
  USE_MESSAGE_ROUTER && USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR ? serviceContainer.gameSaveManager : undefined,
  USE_MESSAGE_ROUTER && USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR ? serviceContainer.gameSaveConfigurationAvailability : undefined,
  USE_MESSAGE_ROUTER && USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR ? serviceContainer.campaignGameSaveConfiguration : undefined,
);
export const mediaCoordinator = new MediaCoordinator(() => driverState.currentState);
export const realtimeSignalingService = new RealtimeSignalingService();
export const peerCoordinator = new PeerCoordinator(new PeerLiteConnectionFactory());
export const sessionCoordinator = new SessionCoordinator(
  serviceContainer.realtimeService,
  serviceContainer.peerManager,
  serviceContainer.analyticsManager
);

// StatefulMacroCoordinator (singleton — runs the locked `launch` tree natively).
// Wired in initializeCoordinators after gameLaunch and pavilion are initialized.
export let statefulMacroCoordinator: StatefulMacroCoordinator | null = null;

// ScheduledScreenshotWatcher (singleton — periodic idle-screen health check)
export let scheduledScreenshotWatcher: ScheduledScreenshotWatcher | null = null;
export const gamingScoreCoordinator = new GamingScoreCoordinator(
  serviceContainer.realtimeService,
  () => machineIdState.currentState || null,
  machineId => new GamingScoreApiService(constants.REALTIME_API_BASE_URL, fetch).read(machineId),
);

// Initialized lazily inside initializeCoordinators when USE_MEDIA_COORDINATOR is true
export let adminPeerHandler: AdminPeerHandler | null = null;
export let localHostSessionCoordinator: LocalHostSessionCoordinator | null = null;
export let macroPackageRuntime: MacroPackageRuntime = null!;

// Beacon coordinator singleton (conditional on USE_BEACON_COORDINATOR)
export const beaconCoordinator: BeaconCoordinator | null = USE_BEACON_COORDINATOR
  ? new BeaconCoordinator({
      realtimeService: serviceContainer.realtimeService,
      getMachineId: () => machineIdState.currentState,
    })
  : null;

// Provisioning coordinator singleton (conditional on USE_PROVISIONING_COORDINATOR)
export const provisioningCoordinator: ProvisioningCoordinator | null = USE_PROVISIONING_COORDINATOR
  ? new ProvisioningCoordinator({
      ipcService: serviceContainer.ipcService,
      realtimeService: serviceContainer.realtimeService,
      getMachineId: () => machineIdState.currentState,
      getHostId: () => appCoordinator.getHostAuthId(),
    })
  : null;

export const nonsoleExitCoordinator = new NonsoleExitCoordinator({
  ipcService: serviceContainer.ipcService,
  getHostId: () => appCoordinator.getHostAuthId(),
});

// Beacon manager singletons (conditional on USE_BEACON_COORDINATOR)
export let displaySourceManager: DisplaySourceManager | null = null;
export let audioManager: AudioManager | null = null;
export let displayOutputManager: DisplayOutputManager | null = null;

// Restart manager singleton (always active)
export let restartManager: RestartManager = null!;

/**
 * Initialize all coordinators
 *
 * Call this once at app startup (before any messages are processed).
 */
export async function initializeCoordinators(): Promise<void> {
  if (!USE_MESSAGE_ROUTER) {
    console.log('[Coordinators] Feature flag disabled, skipping initialization');
    return;
  }

  console.log('[Coordinators] Initializing coordinator-based architecture...');

  try {
    initializeMacroAnalysis(serviceContainer.playjectorService, serviceContainer.realtimeService, realtimeMessageRouter);
    // Initialize AppCoordinator FIRST (calculates static host properties)
    // All other coordinators will read from this single source of truth
    await appCoordinator.init();
    const legacyStoreForGamepads = getStateStore('ipc');
    appCoordinator.registerHostGamepadStore(legacyStoreForGamepads.hostGamepadState);
    console.log('[Coordinators] ✓ AppCoordinator initialized');

    // Initialize ProvisioningCoordinator (pavilion-only, independent of other coordinators)
    if (USE_PROVISIONING_COORDINATOR && provisioningCoordinator) {
      provisioningCoordinator.init();
      provisioningCoordinator.registerMessageHandlers(realtimeMessageRouter);
      provisioningCoordinator.registerPlayjectorHandlers(playjectorMessageRouter);
      console.log('[Coordinators] ✓ ProvisioningCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 ProvisioningCoordinator skipped (feature flag disabled)');
    }

    if (USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR) {
      serviceContainer.initializeGameSaveStorage(realtimeMessageRouter);
    }

    // Initialize GameLaunchCoordinator (if enabled)
    // This must happen before PavilionCoordinator init so PavilionCoordinator can listen to its events
    if (USE_PLAYJECTOR_COORDINATOR) {
      await gameLaunchCoordinator.init(pavilionCoordinator, sessionCoordinator);
      gameLaunchCoordinator.registerReplicationHandler(replicationRouter);
      console.log('[Coordinators] ✓ GameLaunchCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 GameLaunchCoordinator skipped (feature flag disabled)');
    }

    // Initialize SessionCoordinator (manages presence reporting)
    // Pass appCoordinator to read static properties
    // Pass gameLaunchCoordinator and peerCoordinator so it can listen to their events
    // Must be initialized BEFORE PeerCoordinator so PeerCoordinator can listen to streamStopped events
    if (USE_SESSION_COORDINATOR) {
      await sessionCoordinator.init(
        appCoordinator,
        USE_PLAYJECTOR_COORDINATOR ? gameLaunchCoordinator : undefined,
        peerCoordinator
      );
      console.log('[Coordinators] ✓ SessionCoordinator initialized');
      sessionCoordinator.events.joinableChanged.addListener(({ joinable }) => {
        hostState.updateState({
          ...hostState.currentState,
          streamJoinable: joinable,
        });
      });
    } else {
      console.log('[Coordinators] 🚧 SessionCoordinator skipped (feature flag disabled)');
    }

    gamingScoreCoordinator.init();
    console.log('[Coordinators] ✓ GamingScoreCoordinator initialized');

    // Launch, watcher and local Live Detection consume common compiled packages.
    const nativeMacros = serviceContainer.playjectorService.nativeMacros;
    const macroBundleLoader = nativeMacros
      ? new MacroBundleSocketLoader(serviceContainer.realtimeService, realtimeMessageRouter) : undefined;

    // Compiled macro packages: grants, raw upload, native package runs (up to 8 sessions), capture relay.
    // Fed from the main-process IPC stream directly, independent of the local-editor forwarder.
    macroPackageRuntime = initializeMacroPackages({ ipc: serviceContainer.ipcService, realtime: serviceContainer.realtimeService,
      realtimeRouter: realtimeMessageRouter, receive: ipcReceive, receiveMacroPackages: macroPackageReceive,
      ownsVerificationHost: host => host.machineId === machineIdState.currentState
        && (host.kind === 'local' ? localHostSessionCoordinator?.ownsSession(host.sessionId) === true
          : adminPeerHandler?.ownsSession(host.sessionId) === true) });
    console.log('[Coordinators] ✓ Macro package runtime initialized');

    if (USE_MEDIA_COORDINATOR) {
      await mediaCoordinator.init();

      mediaCoordinator.reportDriverStatus();
      driverState.addListener(() => mediaCoordinator.reportDriverStatus());

      // Wire shadowcam health into both matchmaker paths (USE_MESSAGE_ROUTER on/off).
      const shadowcamHealthSource = mediaCoordinator.getShadowcamPresenceMonitor();
      pavilionCoordinator.setShadowcamHealthSource(shadowcamHealthSource);
      setLegacyMatchmakerShadowcamHealthSource(shadowcamHealthSource);

      console.log('[Coordinators] ✓ MediaCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 MediaCoordinator skipped (feature flag disabled)');
    }

    // Live connected user/admin peer count -> PlayJector (for service affinity gating).
    // Declared at function scope so the auth bridge below can resync even though the
    // manager instance is created inside the USE_MEDIA_COORDINATOR block.
    let connectedUsersIpcSyncManager: ConnectedUsersIpcSyncManager | null = null;

    // Initialize PeerCoordinator (manages peer connections and player lifecycle)
    // Subscribes to MediaCoordinator.tracksChanged for track updates to peers
    // Gated by USE_MEDIA_COORDINATOR so peer/admin/input can be disabled independently of SessionCoordinator
    if (USE_MEDIA_COORDINATOR) {
      const peerStack = await initializePeerStack({ appCoordinator, pavilionCoordinator, sessionCoordinator, mediaCoordinator,
        peerCoordinator, realtimeSignalingService, packageRuns: macroPackageRuntime.localHostPackageRuns });
      adminPeerHandler = peerStack.adminPeerHandler;
      localHostSessionCoordinator = peerStack.localHostSessionCoordinator;
      adminPeerHandler.onSessionReleased(sessionId => { void macroPackageRuntime?.verification.release({ kind: 'remote',
        machineId: machineIdState.currentState, sessionId }).catch(() => undefined); });
      connectedUsersIpcSyncManager = peerStack.connectedUsersIpcSyncManager;

      // Admin / cold mobile must not gUM until DMWrapper applies setSource (frame ring bound).
      const ipcService = serviceContainer.ipcService;
      const SHADOWCAM_SOURCE_ACK_TIMEOUT_MS = 5_000;

      const waitForShadowcamEnumerated = async (shadowcamName: string): Promise<void> => {
        const deadline = Date.now() + SHADOWCAM_SOURCE_ACK_TIMEOUT_MS;
        while (Date.now() < deadline) {
          await mediaCoordinator.getDeviceConfig().enumerateDevices();
          const found = mediaCoordinator
            .getDeviceConfig()
            .getShadowcams()
            .some(
              (cam) =>
                cam.displayName?.includes(shadowcamName) || cam.deviceId?.includes(shadowcamName),
            );
          if (found) return;
          await new Promise<void>((resolve) => setTimeout(resolve, 100));
        }
        console.warn(
          `[Coordinators] Shadowcam ${shadowcamName} not enumerated within ${SHADOWCAM_SOURCE_ACK_TIMEOUT_MS}ms`,
        );
      };

      /**
       * Resolves when host-electron records an applied setSource for this camera.
       * Rejects on timeout — do not proceed to gUM.
       */
      const waitForShadowcamSourceAck = (shadowcamName: string, waitStartedAt: number): Promise<void> => {
        const isAcked = () => {
          const boundAtMs = shadowcamBindState.currentState.bindings[shadowcamName]?.boundAtMs;
          return typeof boundAtMs === 'number' && boundAtMs >= waitStartedAt;
        };
        if (isAcked()) return Promise.resolve();

        return new Promise<void>((resolve, reject) => {
          let settled = false;
          const finish = (fn: () => void) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            if (listenerId) shadowcamBindState.removeListener(listenerId);
            fn();
          };
          const listenerId = shadowcamBindState.addListener(() => {
            if (isAcked()) finish(resolve);
          });
          const timeoutId = setTimeout(() => {
            finish(() =>
              reject(
                new Error(
                  `Shadowcam ${shadowcamName} setSource ack timed out after ${SHADOWCAM_SOURCE_ACK_TIMEOUT_MS}ms`,
                ),
              ),
            );
          }, SHADOWCAM_SOURCE_ACK_TIMEOUT_MS);
          if (isAcked()) finish(resolve);
        });
      };

      const beforeAdminMediaCapture = async (): Promise<void> => {
        // Control-plane only: arm adminSession. Cold Admin binds Cam03 via activateShadowcam.
        const hostId = appCoordinator.getHostAuthId();
        ipcService.send(
          generators.input.toggleFilters(hostId, { keyboard: false, mouse: false, gamepad: false }),
        );
        console.log('[Coordinators] Admin capture prep: input filters disabled (arms PlayJector admin session)');
      };

      const disarmAdminMediaCapture = (): void => {
        // exitGuestModeAfterAdminSession=false: restoring filters must not tear down Nonsole.
        const hostId = appCoordinator.getHostAuthId();
        ipcService.send(
          generators.input.toggleFilters(hostId, {
            keyboard: true,
            mouse: true,
            gamepad: true,
            exitGuestModeAfterAdminSession: false,
          }),
        );
        console.log('[Coordinators] Admin capture prep disarmed (filters on, Nonsole retained)');
      };

      const activateShadowcam = async (shadowcamName: string): Promise<void> => {
        console.log(`[Coordinators] Activating shadowcam ${shadowcamName} via Playjector`);
        const waitStartedAt = Date.now();
        ipcService.send(generators.camera.startStopToggle(shadowcamName, true));
        ipcService.send(generators.camera.getSources());
        void waitForShadowcamEnumerated(shadowcamName).catch(() => undefined);
        await waitForShadowcamSourceAck(shadowcamName, waitStartedAt);
        console.log(`[Coordinators] Shadowcam ${shadowcamName} applied setSource ack received`);
      };

      const deactivateShadowcam = async (shadowcamName: string): Promise<void> => {
        console.log(`[Coordinators] Deactivating on-demand shadowcam ${shadowcamName}`);
        ipcService.send(generators.camera.startStopToggle(shadowcamName, false));
      };

      const ensureAudioCasterDefault = async (): Promise<void> => {
        const hostAuthId = appCoordinator.getHostAuthId();
        console.log('[AUDIO-BUG-12009:Coordinators] Ensuring AudioCaster default via broadcastStatus(streaming)');
        ipcService.send(generators.hub.broadcastStatus('streaming', hostAuthId));
        connectedUsersIpcSyncManager?.resync();
      };

      peerCoordinator.installAdminCapturePipeline({
        beforeAdminMediaCapture,
        disarmAdminMediaCapture,
        activateShadowcam,
        deactivateShadowcam,
        ensureAudioCasterDefault,
      });
      console.log('[Coordinators] ✓ PeerCoordinator initialized (with InputManager, GamepadManager, AdminPeerHandler)');
    } else {
      console.log('[Coordinators] 🚧 PeerCoordinator skipped (USE_MEDIA_COORDINATOR disabled)');
    }

    // Initialize PavilionCoordinator (MVP - fully functional)
    // Pass appCoordinator to read static properties when sending HOST_CONFIG
    // Pass gameLaunchCoordinator so it can listen to campaign switch events
    // Pass sessionCoordinator so it can orchestrate stream start on match found
    pavilionCoordinator.init(
      appCoordinator,
      USE_PLAYJECTOR_COORDINATOR ? gameLaunchCoordinator : undefined,
      USE_SESSION_COORDINATOR ? sessionCoordinator : undefined
    );
    pavilionCoordinator.registerMessageHandlers(realtimeMessageRouter);
    pavilionCoordinator.registerHostMessageHandlers(realtimeMessageRouter);
    nonsoleExitCoordinator.registerMessageHandlers(realtimeMessageRouter);
    console.log('[Coordinators] ✓ PavilionCoordinator initialized');

    const sanitizationMonitor = new NonsoleSanitizationMonitor(() => sanitizationState.currentState);
    pavilionCoordinator.setSanitizationHealthSource(sanitizationMonitor);
    sanitizationState.addListener(() => sanitizationMonitor.notifyIfChanged());

    // Initialize StatefulMacroCoordinator: runs the campaign's locked `launch`
    // tree natively in Playjector. Without a native bridge every stateful
    // launch fails with `native_macro_unavailable`.
    // One loader serves both launch and watcher: a single MACROBUNDLE_RESPONSE
    // handler and a single reconnect hook. Launch and watcher never load at the
    // same time (the watcher skips ticks while a macro run is active).
    if (USE_PLAYJECTOR_COORDINATOR) {
      statefulMacroCoordinator = new StatefulMacroCoordinator({
        nativeLaunch: nativeMacros && macroBundleLoader
          ? new NativeMacroLaunchManager(nativeMacros, macroBundleLoader) : undefined,
        gameLaunchCoordinator,
        pavilionCoordinator,
        packageSessions: macroPackageRuntime.sessions,
      });
      statefulMacroCoordinator.init();
      // Mirror macro-active state to the replicated store so the debug indicator
      // shows on the host and (via player replication) on the remote view.
      // Tie the subscription's lifetime to the coordinator so it's torn down
      // with it rather than leaking past a destroy().
      statefulMacroCoordinator.registerDisposer(
        wireMacroRunningReplication(statefulMacroCoordinator, macroRunningState),
      );
      console.log('[Coordinators] ✓ StatefulMacroCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 StatefulMacroCoordinator skipped (USE_PLAYJECTOR_COORDINATOR disabled)');
    }

    // Initialize ScheduledScreenshotWatcher (periodic idle-screen health check).
    // Each check runs the locked `watcher` tree natively in `local-shadow`
    // mode, so it needs the native bridge, the bundle loader, and the launch
    // coordinator's run state to skip ticks while a macro run is active.
    if (USE_PLAYJECTOR_COORDINATOR && USE_SESSION_COORDINATOR && nativeMacros && macroBundleLoader) {
      const checkRunner = new WatcherCheckRunner({
        loader: macroBundleLoader,
        bridge: nativeMacros,
        busy: () => statefulMacroCoordinator?.state.active ?? false,
        newRunId: () => crypto.randomUUID(),
      });
      scheduledScreenshotWatcher = new ScheduledScreenshotWatcher({
        checkRunner,
        gameLaunchCoordinator,
        pavilionCoordinator,
        sessionCoordinator,
      });
      scheduledScreenshotWatcher.init();
      wireScheduledWatcherEvents(scheduledScreenshotWatcher, pavilionCoordinator);
      console.log('[Coordinators] ✓ ScheduledScreenshotWatcher initialized');
    } else {
      console.log('[Coordinators] 🚧 ScheduledScreenshotWatcher skipped (feature flags disabled or no native macro bridge)');
    }

    // TODO-STRANGLER-FIG: Temporary adapter for authState until IdentityCoordinator is built
    // PavilionCoordinator and AppCoordinator need authId.
    // In the new architecture, IdentityCoordinator would own authState and emit events.
    authState.addListener((state) => {
      if (state.hostAuthId) {
        appCoordinator.updateHostAuthId(state.hostAuthId);
        pavilionCoordinator.updateAuthId(state.hostAuthId);
        // Re-send the current connected-user count once the host auth id is (re)bridged.
        // The initial report may have gone out with an empty/experimental id and then be
        // suppressed because the peer set hadn't changed; resync forces a fresh report with
        // the valid id.
        connectedUsersIpcSyncManager?.resync();
      }
    });
    if (authState.currentState.hostAuthId) {
      appCoordinator.updateHostAuthId(authState.currentState.hostAuthId);
      pavilionCoordinator.updateAuthId(authState.currentState.hostAuthId);
      // Resync after the initial auth-state snapshot is applied: the manager's own
      // start() already sent an initial report before this branch ran (with the empty or
      // experimental host id), so if the authenticated id is already present and the peer
      // count doesn't change, PlayJector wouldn't otherwise receive the authenticated count.
      connectedUsersIpcSyncManager?.resync();
    }

    // Initialize BeaconCoordinator (conditional)
    if (USE_BEACON_COORDINATOR && beaconCoordinator) {
      const beacon = beaconCoordinator;
      beacon.init(appCoordinator, sessionCoordinator, peerCoordinator);
      beacon.registerMessageHandlers(realtimeMessageRouter);

      // Bridge authState to BeaconCoordinator (same pattern as PavilionCoordinator)
      if (authState.currentState.hostAuthId) {
        beacon.updateAuthId(authState.currentState.hostAuthId);
      }
      authState.addListener((state) => {
        if (state.hostAuthId) {
          beacon.updateAuthId(state.hostAuthId);
        }
      });

      console.log('[Coordinators] ✓ BeaconCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 BeaconCoordinator skipped (feature flag disabled)');
    }

    // Beacon and Pavilion share the same capture-source managers. Prime does not.
    if (USE_MEDIA_COORDINATOR && (USE_BEACON_COORDINATOR || USE_PAVILION_COORDINATOR)) {
      const beaconMedia = new BeaconMediaManagerInitializer({
        ipcService: serviceContainer.ipcService,
        app: appCoordinator,
        media: mediaCoordinator,
        session: sessionCoordinator,
        replication: replicationRouter,
        shadowcams: getStateStore('ipc').activeShadowcamState,
      }).initialize();
      displaySourceManager = beaconMedia.displaySourceManager;
      audioManager = beaconMedia.audioManager;
      displayOutputManager = beaconMedia.displayOutputManager;
      console.log('[Coordinators] ✓ Capture source managers initialized (DisplaySource, Audio, DisplayOutput)');
    }

    // TODO-STRANGLER-FIG: Bridge loginProfileState to AppCoordinator.authState
    loginProfileState.addListener((state) => {
      appCoordinator.updateAuthState(state.accessToken);
    });
    if (loginProfileState.currentState.accessToken) {
      appCoordinator.updateAuthState(loginProfileState.currentState.accessToken);
    }

    console.log('[Coordinators] All coordinators initialized successfully');

    restartManager = initRestartManager(playjectorMessageRouter);
  } catch (error) {
    console.error('[Coordinators] Failed to initialize coordinators:', error);
    throw error;
  }
}

// All coordinator instances are exported at their declaration site above.
